/**
 * O painel do eixo Arquivo (E84.F2): o que o próprio arquivo declara sobre si.
 *
 * Módulo próprio porque `QuadroPorEixosPaineis` já passa do teto de 200 linhas
 * do handbook §8. Mesma regra dos demais painéis: a frase do que o eixo
 * confronta, os itens que o backend devolveu, e a ficha lida do arquivo — sem
 * alegação nova. Três estados, os três declarados: exame anterior ao estágio,
 * arquivo sem dicionário legível, ficha lida.
 *
 * As datas saem como o arquivo as escreveu — com segundos (no metadado, os
 * segundos são o achado) e com o fuso declarado, sem converter para o fuso do
 * navegador: converter esconderia justamente o "fuso não declarado" que o
 * laudo precisa dizer.
 */
import { ptBR } from '@/i18n/pt-BR';
import type { MetadadosDaPecaData } from '@/lib/exame_eletronico';

import { origemDoResultado } from '@/lib/exame_eletronico_arquivo_unico';
import { formatarDeclarado } from '@/lib/exame_eletronico_datas_declaradas';

import { ComoLer, FatosDoEixo, type PainelDoEixoProps } from './QuadroPorEixosPaineis';
import { OrigemDoDocumento } from './QuadroVerificacaoOrigem';

export { formatarDeclarado };
import { Titulo } from './QuadroVerificacaoTabelas';

const t = ptBR.exameEletronico.eixos;
const ta = ptBR.exameEletronico.quadro.arquivo;

function LinhaDaFicha({ rotulo, valor }: { rotulo: string; valor: string }): JSX.Element {
  return (
    <div className="flex flex-wrap gap-x-sc-2 text-body-sm">
      <dt className="text-fg-muted">{rotulo}:</dt>
      <dd className="text-fg">{valor}</dd>
    </div>
  );
}

function FichaDoArquivo({ ficha }: { ficha: MetadadosDaPecaData }): JSX.Element {
  const fonte = ficha.fonte_das_datas ? ta.fonteDasDatasOpcoes[ficha.fonte_das_datas] : null;
  const ou = (valor: string | null | undefined): string => valor || ta.naoInformado;
  return (
    <section data-testid="quadro-arquivo-ficha">
      <Titulo>{ta.ficha}</Titulo>
      <dl className="space-y-sc-1">
        <LinhaDaFicha rotulo={ta.criadoEm} valor={formatarDeclarado(ficha.criado_em)} />
        <LinhaDaFicha rotulo={ta.modificadoEm} valor={formatarDeclarado(ficha.modificado_em)} />
        <LinhaDaFicha rotulo={ta.fonteDasDatas} valor={ou(fonte)} />
        <LinhaDaFicha rotulo={ta.produtor} valor={ou(ficha.produtor)} />
        <LinhaDaFicha rotulo={ta.criador} valor={ou(ficha.criador)} />
        <LinhaDaFicha rotulo={ta.autor} valor={ou(ficha.autor)} />
        <LinhaDaFicha rotulo={ta.titulo} valor={ou(ficha.titulo)} />
        <LinhaDaFicha rotulo={ta.salvamentos} valor={String(ficha.salvamentos)} />
      </dl>
      <p className="mt-sc-2 max-w-prose text-caption text-fg-dim">{ta.declaratorios}</p>
    </section>
  );
}

export function PainelArquivo({ resultado }: PainelDoEixoProps): JSX.Element {
  const apurado = resultado.metadados ?? null;
  return (
    <div className="space-y-sc-4" data-testid="eixo-painel-arquivo">
      <ComoLer texto={t.confronta.arquivo} testId="como-ler-arquivo" />
      <OrigemDoDocumento origem={origemDoResultado(resultado)} />
      {apurado === null ? (
        <p className="text-body-sm text-fg-dim" data-testid="quadro-arquivo-nao-examinado">
          {ta.naoExaminado}
        </p>
      ) : (
        <>
          <FatosDoEixo itens={apurado.itens} testId="quadro-arquivo" />
          {apurado.metadados ? (
            <FichaDoArquivo ficha={apurado.metadados} />
          ) : (
            <p className="text-body-sm text-fg-dim" data-testid="quadro-arquivo-sem-dicionario">
              {ta.semDicionario}
            </p>
          )}
        </>
      )}
    </div>
  );
}
